import { SITE } from "@/config";
import { Chapter, Reveal } from "@/components/Reveal";
import { PrimaryCTA } from "@/components/CTA";

const CASES = [
  {
    n: "01",
    name: "NAOMI ESTETICA & BENESSERE",
    sector: "Beauty",
    situation: "Team fortemente dipendente dalla titolare e fatturato in fase di stallo.",
    actions: [
      "Formazione della titolare",
      "Formazione del team",
      "Responsabilizzazione delle persone",
      "Sistemi incentivanti",
      "Marketing esperienziale",
    ],
    change: "Team più motivato e progressivamente più autonomo. Minore dipendenza dalla titolare.",
    metric: "+142,5%",
    metricLabel: "Fatturato",
    period: "Marzo 2026 vs Marzo 2025",
  },
  {
    n: "02",
    name: "MASSERIA MADONNA DELL'ARCO",
    sector: "Ho.Re.Ca.",
    situation: null,
    actions: [
      "Formazione dell'imprenditore",
      "Analisi delle attitudini dei collaboratori",
      "Formazione del personale",
      "Percorso manageriale per l'imprenditore",
    ],
    change: "Collaboratori maggiormente allineati all'azienda.",
    metric: "+43%",
    metricLabel: "Fatturato",
    period: "Luglio 2026 vs Luglio 2025",
  },
  {
    n: "03",
    name: "LA BOTTEGA DELLA BELLEZZA",
    sector: "Beauty",
    situation: "Difficoltà nella gestione finanziaria e nella generazione di fatturato e utile.",
    actions: [
      "Formazione dell'imprenditrice",
      "Formazione specifica su finanze e numeri",
      "Consulenza marketing strategico",
      "Consulenza finanziaria",
    ],
    change: "Maggiore consapevolezza imprenditoriale. Miglioramento nella gestione economica dell'azienda.",
    metric: "+110%",
    metricLabel: "Utile",
    period: "Aprile vs stesso mese dell'anno precedente",
  },
];

const Label = ({ children }) => (
  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/35">{children}</p>
);

const CaseCard = ({ c }) => (
  <article
    data-testid={`case-card-${c.n}`}
    className="gold-glow-hover flex h-full flex-col border border-white/10 bg-ink p-8 text-white lg:p-9"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
        Case study {c.n}
      </span>
      <span className="border border-accent/40 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-accent">
        {c.sector}
      </span>
    </div>

    <h3 className="mt-6 font-display text-lg font-bold tracking-tight">{c.name}</h3>

    {c.situation && (
      <div className="mt-7">
        <Label>Situazione</Label>
        <p className="mt-2 text-sm leading-relaxed text-white/55">{c.situation}</p>
      </div>
    )}

    <div className="mt-6">
      <Label>Intervento</Label>
      <ul className="mt-2 space-y-1.5">
        {c.actions.map((a) => (
          <li key={a} className="flex items-baseline gap-2 text-sm leading-relaxed text-white/55">
            <span className="text-accent" aria-hidden="true">—</span>
            {a}
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-6 pb-8">
      <Label>Cambiamento</Label>
      <p className="mt-2 text-sm leading-relaxed text-white/55">{c.change}</p>
    </div>

    <div className="mt-auto border-t border-accent/25 pt-7">
      <Label>Risultato</Label>
      <p className="mt-3 bg-gradient-to-b from-[#E5C158] to-[#9F761A] bg-clip-text font-display text-6xl font-extrabold tracking-tighter text-transparent lg:text-7xl">
        {c.metric}
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-accent">{c.metricLabel}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">{c.period}</p>
    </div>
  </article>
);

const Cases = () => (
  <section id="risultati" data-testid="cases-section" className="bg-fog py-28 text-ink lg:py-40">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
      <Chapter index="05" label="Risultati" />
      <Reveal>
        <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
          I numeri sono la conseguenza.
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-8 max-w-2xl">
        <p className="text-base leading-relaxed text-ink/60 md:text-lg">
          Prima delle percentuali vengono le persone, l'organizzazione e le decisioni. Quando queste
          cambiano, cambiano anche i risultati.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {CASES.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <CaseCard c={c} />
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="gold-glow-hover border border-white/10 bg-ink p-8 text-white lg:p-14" data-testid="cases-bridge">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <h3 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
                Ogni azienda ha un punto da cui partire.
              </h3>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-white/60">
                Prima di scegliere cosa migliorare, bisogna capire dove l'azienda dipende ancora
                troppo dall'imprenditore.
              </p>
            </div>
            <PrimaryCTA href={SITE.bf360} testid="cases-bridge-cta" className="shrink-0">
              Scopri il tuo punto di partenza
            </PrimaryCTA>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Cases;
